import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { examAPI } from '../services/api';
import {
  fetchExamsStart,
  fetchExamsSuccess,
  fetchExamsFailure,
} from '../store/slices/examSlice';

const CreateExam = () => {
  const [examData, setExamData] = useState({
    title: '',
    description: '',
    time_limit: 60,
    questions: [
      {
        question_text: '',
        options: [
          { letter: 'A', text: '' },
          { letter: 'B', text: '' }
        ],
        correct_answer: 'A',
        weight_percentage: 100
      },
    ],
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.exam);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleExamChange = (e) => {
    setExamData({
      ...examData,
      [e.target.name]: e.target.value,
    });
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...examData.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    setExamData({
      ...examData,
      questions: updatedQuestions,
    });
  };

  const handleOptionChange = (questionIndex, optionIndex, field, value) => {
    const updatedQuestions = [...examData.questions];
    updatedQuestions[questionIndex].options[optionIndex][field] = value;
    setExamData({
      ...examData,
      questions: updatedQuestions,
    });
  };

  const addOption = (questionIndex) => {
    const updatedQuestions = [...examData.questions];
    const currentOptions = updatedQuestions[questionIndex].options;
    const lastOption = currentOptions[currentOptions.length - 1];
    const nextLetter = String.fromCharCode(lastOption.letter.charCodeAt(0) + 1);
    
    if (nextLetter <= 'F') {
      updatedQuestions[questionIndex].options.push({
        letter: nextLetter,
        text: ''
      });
      setExamData({
        ...examData,
        questions: updatedQuestions,
      });
    }
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...examData.questions];
    const question = updatedQuestions[questionIndex];
    
    // Don't allow removing if only 2 options left
    if (question.options.length <= 2) return;
    
    // Remove the option
    question.options.splice(optionIndex, 1);
    
    // Update correct answer if it was the removed option
    if (question.correct_answer === question.options[optionIndex]?.letter) {
      question.correct_answer = question.options[0].letter;
    }
    
    setExamData({
      ...examData,
      questions: updatedQuestions,
    });
  };

  const addQuestion = () => {
    const weightPerQuestion = 100 / (examData.questions.length + 1);
    const updatedQuestions = examData.questions.map(q => ({
      ...q,
      weight_percentage: weightPerQuestion
    }));

    setExamData({
      ...examData,
      questions: [
        ...updatedQuestions,
        {
          question_text: '',
          options: [
            { letter: 'A', text: '' },
            { letter: 'B', text: '' }
          ],
          correct_answer: 'A',
          weight_percentage: weightPerQuestion
        },
      ],
    });
  };

  const removeQuestion = (index) => {
    const updatedQuestions = examData.questions.filter((_, i) => i !== index);
    const weightPerQuestion = 100 / updatedQuestions.length;
    
    const reweightedQuestions = updatedQuestions.map(q => ({
      ...q,
      weight_percentage: weightPerQuestion
    }));

    setExamData({
      ...examData,
      questions: reweightedQuestions,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(fetchExamsStart());

    try {
      const response = await examAPI.createExam(examData);
      if (response.data.success) {
        navigate('/');
      } else {
        dispatch(fetchExamsFailure(response.data.message || 'Failed to create exam'));
      }
    } catch (error) {
      console.error('Exam creation error:', error);
      dispatch(fetchExamsFailure(
        error.response?.data?.message || 
        error.message || 
        'Failed to create exam'
      ));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Create New Exam
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Exam Title"
                name="title"
                value={examData.title}
                onChange={handleExamChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={examData.description}
                onChange={handleExamChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Time Limit (minutes)"
                name="time_limit"
                value={examData.time_limit}
                onChange={handleExamChange}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Questions
              </Typography>
            </Grid>

            {examData.questions.map((question, questionIndex) => (
              <Grid item xs={12} key={questionIndex}>
                <Paper sx={{ p: 2, position: 'relative' }}>
                  <IconButton
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    onClick={() => removeQuestion(questionIndex)}
                    disabled={examData.questions.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label={`Question ${questionIndex + 1}`}
                        value={question.question_text}
                        onChange={(e) =>
                          handleQuestionChange(questionIndex, 'question_text', e.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Options
                      </Typography>
                      {question.options.map((option, optionIndex) => (
                        <Box key={option.letter} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <TextField
                            required
                            fullWidth
                            label={`Option ${option.letter}`}
                            value={option.text}
                            onChange={(e) =>
                              handleOptionChange(questionIndex, optionIndex, 'text', e.target.value)
                            }
                          />
                          <IconButton
                            onClick={() => removeOption(questionIndex, optionIndex)}
                            disabled={question.options.length <= 2}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => addOption(questionIndex)}
                        disabled={question.options.length >= 6}
                        sx={{ mt: 1 }}
                      >
                        Add Option
                      </Button>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Correct Answer</InputLabel>
                        <Select
                          value={question.correct_answer}
                          label="Correct Answer"
                          onChange={(e) =>
                            handleQuestionChange(
                              questionIndex,
                              'correct_answer',
                              e.target.value
                            )
                          }
                        >
                          {question.options.map((option) => (
                            <MenuItem key={option.letter} value={option.letter}>
                              Option {option.letter}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        type="number"
                        label="Weight (%)"
                        value={question.weight_percentage}
                        onChange={(e) =>
                          handleQuestionChange(
                            questionIndex,
                            'weight_percentage',
                            parseFloat(e.target.value)
                          )
                        }
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addQuestion}
                  variant="outlined"
                >
                  Add Question
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'Creating Exam...' : 'Create Exam'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateExam; 